<template>
  <div class="review-management">
    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    
    <!-- Header -->
    <div class="page-header">
      <h1>Review Management</h1>
      <p>Approve, reject, and manage product reviews</p>
      <div v-if="!canInteract" class="authorization-alert">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>You are not authorized to interact with reviews. Only support and super admin can manage reviews.</span>
      </div>
    </div>

    <!-- Filters and Stats -->
    <div class="filters-section">
      <div class="stats-cards">
        <div class="stat-card pending">
          <div class="stat-icon">
            <i class="fa-solid fa-clock"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.pending }}</h3>
            <p>Pending Reviews</p>
          </div>
        </div>
        <div class="stat-card approved">
          <div class="stat-icon">
            <i class="fa-solid fa-check-circle"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.approved }}</h3>
            <p>Approved Reviews</p>
          </div>
        </div>
        <div class="stat-card rejected">
          <div class="stat-icon">
            <i class="fa-solid fa-times-circle"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.rejected }}</h3>
            <p>Rejected Reviews</p>
          </div>
        </div>
        <div class="stat-card total">
          <div class="stat-icon">
            <i class="fa-solid fa-comments"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.total }}</h3>
            <p>Total Reviews</p>
          </div>
        </div>
      </div>

      <div class="filter-controls">
        <div class="filter-group">
          <label>Status:</label>
          <select v-model="filters.status" @change="loadReviews">
            <option value="all">All Reviews</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Rating:</label>
          <select v-model="filters.rating" @change="loadReviews">
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Product Type:</label>
          <select v-model="filters.productType" @change="loadReviews">
            <option value="all">All Products</option>
            <option v-for="type in productTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>

        <button class="btn btn-secondary" @click="resetFilters">
          <i class="fa-solid fa-refresh"></i>
          Reset Filters
        </button>
      </div>
    </div>

    <!-- Reviews Table -->
    <div class="reviews-table-container">
      <div class="table-header">
        <h3 style="color: var(--text-color1)">Reviews</h3>
        <div class="table-actions">
          <button 
            class="btn btn-primary" 
            @click="bulkApprove"
            :disabled="selectedReviews.length === 0 || !canInteract"
          >
            <i class="fa-solid fa-check"></i>
            Approve Selected ({{ selectedReviews.length }})
          </button>
          <button 
            class="btn btn-danger" 
            @click="bulkReject"
            :disabled="selectedReviews.length === 0 || !canInteract"
          >
            <i class="fa-solid fa-times"></i>
            Reject Selected ({{ selectedReviews.length }})
          </button>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="reviews-table">
          <thead>
            <tr>
              <th class="checkbox-col">
                <input 
                  type="checkbox" 
                  :checked="allSelected"
                  @change="toggleSelectAll"
                  aria-label="Select all reviews"
                >
              </th>
              <th>Product</th>
              <th>Customer</th>
              <th>Rating</th>
              <th>Review</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="review in paginatedReviews" :key="review.review_id">
              <td class="checkbox-col">
                <input 
                  type="checkbox" 
                  :checked="selectedReviews.includes(review.review_id)"
                  @change="toggleReviewSelection(review.review_id)"
                  :aria-label="`Select review ${review.review_id}`"
                >
              </td>
              <td class="product-info">
                <div class="product-image">
                  <img
                    v-if="review.product_image"
                    :src="review.product_image"
                    :alt="review.product_name"
                    loading="lazy"
                    decoding="async"
                    @error="handleImageError"
                  >
                  <div v-else class="product-image-placeholder">
                    <i class="fa-solid fa-image"></i>
                  </div>
                </div>
                <div class="product-details">
                  <strong style="color: var(--text-color1)">{{ review.product_name || 'Unknown Product' }}</strong>
                  <span v-if="review.product_sku" class="product-sku">SKU: {{ review.product_sku }}</span>
                </div>
              </td>
              <td class="customer-info">
                <div class="customer-avatar">
                  {{ review.customer_name.charAt(0).toUpperCase() }}
                </div>
                <div class="customer-details">
                  <strong style="color: var(--text-color1)">{{ review.customer_name }}</strong>
                </div>
              </td>
              <td class="rating">
                <div class="stars">
                  <i 
                    v-for="star in 5" 
                    :key="star"
                    class="fa-star"
                    :class="star <= review.rating ? 'fa-solid filled' : 'fa-regular'"
                  ></i>
                </div>
              </td>
              <td class="review-content">
                <p class="review-text">{{ previewText(review.review_text) }}</p>
              </td>
              <td class="status">
                <span class="status-badge" :class="review.status">
                  {{ review.status }}
                </span>
              </td>
              <td class="actions">
                <button 
                  class="btn btn-sm btn-success" 
                  @click="approveReview(review.review_id)"
                  :disabled="review.status === 'approved' || !canInteract"
                  title="Approve Review"
                >
                  <i class="fa-solid fa-check"></i>
                </button>
                <button 
                  class="btn btn-sm btn-danger" 
                  @click="rejectReview(review.review_id)"
                  :disabled="review.status === 'rejected' || !canInteract"
                  title="Reject Review"
                >
                  <i class="fa-solid fa-times"></i>
                </button>
                <button 
                  class="btn btn-sm btn-info" 
                  @click="viewReviewDetails(review)"
                  title="View Details"
                >
                  <i class="fa-solid fa-eye"></i>
                </button>
                <button 
                  class="btn btn-sm btn-warning" 
                  @click="deleteReview(review.review_id)"
                  :disabled="!canInteract"
                  title="Delete Review"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="reviews.length === 0" class="empty-state">
          <i class="fa-solid fa-comments-slash"></i>
          <h4>No reviews found</h4>
          <p>There are no reviews matching your current filters.</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="reviews.length > 0">
        <button 
          class="btn btn-outline" 
          @click="previousPage"
          :disabled="currentPage === 1"
        >
          <i class="fa-solid fa-chevron-left"></i>
          Previous
        </button>
        
        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        
        <button 
          class="btn btn-outline" 
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          Next
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Review Details Modal -->
    <div v-if="selectedReview" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Review Details</h3>
          <button class="close-btn" @click="closeModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="review-details">
            <div class="detail-section">
              <h4>Product Information</h4>
              <div class="product-detail">
                <img
                  v-if="selectedReview.product_image"
                  :src="selectedReview.product_image"
                  :alt="selectedReview.product_name"
                  loading="lazy"
                  decoding="async"
                  @error="handleImageError"
                >
                <div v-else class="product-image-placeholder large">
                  <i class="fa-solid fa-image"></i>
                </div>
                <div>
                  <strong>{{ selectedReview.product_name || 'Unknown Product' }}</strong>
                  <p v-if="selectedReview.product_type">Type: {{ selectedReview.product_type }}</p>
                  <p v-if="selectedReview.product_sku">SKU: {{ selectedReview.product_sku }}</p>
                  <p v-if="selectedReview.product_id">Product ID: {{ selectedReview.product_id }}</p>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h4>Customer Information</h4>
              <div class="customer-detail">
                <div class="customer-avatar large">
                  {{ selectedReview.customer_name.charAt(0).toUpperCase() }}
                </div>
                <div>
                  <strong>{{ selectedReview.customer_name }}</strong>
                  <p v-if="selectedReview.customer_joined">Member since: {{ formatDate(selectedReview.customer_joined) }}</p>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h4>Review Content</h4>
              <div class="rating-display">
                <div class="stars">
                  <i 
                    v-for="star in 5" 
                    :key="star"
                    class="fa-star"
                    :class="star <= selectedReview.rating ? 'fa-solid filled' : 'fa-regular'"
                  ></i>
                </div>
                <span class="rating-text">{{ getRatingText(selectedReview.rating) }}</span>
              </div>
              <h5>{{ selectedReview.title }}</h5>
              <p class="review-fulltext">{{ selectedReview.review_text }}</p>
            </div>

            <div class="detail-section">
              <h4>Review Metrics</h4>
              <div class="metrics">
                <div class="metric">
                  <span class="metric-label">Helpful Votes:</span>
                  <span class="metric-value">{{ selectedReview.helpful_count }}</span>
                </div>
                <div class="metric">
                  <span class="metric-label">Status:</span>
                  <span class="metric-value status-badge" :class="selectedReview.status">
                    {{ selectedReview.status }}
                  </span>
                </div>
                <div class="metric">
                  <span class="metric-label">Submitted:</span>
                  <span class="metric-value">{{ formatDate(selectedReview.created_at) }}</span>
                </div>
                <div class="metric">
                  <span class="metric-label">Last Updated:</span>
                  <span class="metric-value">{{ selectedReview.updated_at ? formatDate(selectedReview.updated_at) : (selectedReview.created_at ? formatDate(selectedReview.created_at) : 'N/A') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            class="btn btn-success" 
            @click="approveReview(selectedReview.review_id)"
            :disabled="selectedReview.status === 'approved' || !canInteract"
          >
            <i class="fa-solid fa-check"></i>
            Approve Review
          </button>
          <button 
            class="btn btn-danger" 
            @click="rejectReview(selectedReview.review_id)"
            :disabled="selectedReview.status === 'rejected' || !canInteract"
          >
            <i class="fa-solid fa-times"></i>
            Reject Review
          </button>
          <button class="btn btn-outline" @click="closeModal">
            Close
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/**
 * ReviewManagement Component
 * 
 * Admin component for managing product reviews. Provides comprehensive review management
 * functionality including approval, rejection, deletion, and bulk operations.
 * 
 * Features:
 * - View all reviews with filtering (status, rating, product type)
 * - Approve/reject individual or bulk reviews
 * - Delete reviews permanently
 * - View detailed review information in modal
 * - Statistics dashboard (pending, approved, rejected, total)
 * - Role-based access control (support and super_admin only)
 * - Toast notifications for all operations
 * - Responsive design optimized for admin panel layout
 * 
 * Component Structure:
 * - Child component of adminmain.vue
 * - Displayed alongside sidebar (side_bar.vue) and navbar (navbar.vue)
 * - Uses adminAuth store for authentication and authorization
 * 
 * API Endpoints:
 * - GET /review/admin/reviews - List reviews with filters
 * - GET /review/admin/reviews/stats - Get review statistics
 * - PUT /review/admin/reviews/{id}/approve - Approve single review
 * - PUT /review/admin/reviews/{id}/reject - Reject single review
 * - PUT /review/admin/reviews/bulk-approve - Bulk approve reviews
 * - PUT /review/admin/reviews/bulk-reject - Bulk reject reviews
 * - DELETE /review/admin/reviews/{id} - Delete review
 * 
 * @component ReviewManagement
 * @requires @/authen/adminAuth - useAdminAuthStore for authentication
 * @requires @/config/api - buildApiUrl for API endpoint construction
 * @requires @/components/ui/ToastContainer - Toast notification component
 * 
 * @example
 * <ReviewManagement />
 */
import { buildApiUrl } from "@/config/api";
import { useAdminAuthStore } from "@/authen/adminAuth";
import ToastContainer from '@/components/ui/ToastContainer.vue';
import globalLoadingMixin from '@/mixins/globalLoadingMixin';
import toastMixin from '@/mixins/toastMixin';

export default {
  name: 'ReviewManagement',
  loadingMessage: 'Loading reviews...',
  mixins: [toastMixin, globalLoadingMixin],

  components: {
    ToastContainer
  },
  
  data() {
    return {
      loading: false,
      reviews: [],
      selectedReviews: [],
      selectedReview: null,
      filters: {
        status: 'pending',
        rating: 'all',
        productType: 'all'
      },
      stats: {
        pending: 0,
        approved: 0,
        rejected: 0,
        total: 0
      },
      pagination: {
        currentPage: 1,
        pageSize: 10
      },
      productTypes: [
        { value: 'football', label: 'Football' },
        { value: 'basketball', label: 'Basketball' },
        { value: 'gym', label: 'Gym & Fitness' },
        { value: 'athletics', label: 'Athletics' },
        { value: 'swimming', label: 'Swimming' },
        { value: 'cycling', label: 'Cycling' }
      ],
    }
  },

  computed: {
    allSelected() {
      return this.paginatedReviews.length > 0 && 
             this.paginatedReviews.every(review => 
               this.selectedReviews.includes(review.review_id)
             );
    },
    paginatedReviews() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.reviews.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.reviews.length / this.pagination.pageSize);
    },
    currentPage() {
      return this.pagination.currentPage;
    },
    canInteract() {
      const adminStore = useAdminAuthStore();
      const role = (adminStore.admin?.role || '').toLowerCase();
      return role === 'support' || role === 'super_admin';
    }
  },

  mounted() {
    const adminStore = useAdminAuthStore();
    adminStore.checkToken();
    
    if (!adminStore.isAuthenticated) {
      // Toast will be shown in loadReviews if needed
      return;
    }
    
    this.loadReviews();
    this.loadStats();
  },

  methods: {
    /**
     * Load reviews from API with current filter settings
     * Handles authentication, error states, and session expiration
     * @async
     * @returns {Promise<void>}
     */
    async loadReviews() {
      this.loading = true;
      try {
        const adminStore = useAdminAuthStore();
        adminStore.checkToken();
        
        if (!adminStore.isAuthenticated) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to access review management');
          return;
        }
        
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to access review management');
          return;
        }
        
        const apiUrl = buildApiUrl(`/review/admin/reviews?${this.buildQueryString()}`);
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session Expired', 'Your session has expired. Please log in again.');
            adminStore.logout();
            this.$router.push({ name: 'AdminLogin' });
            return;
          }
          throw new Error('Failed to load reviews');
        }
        
        this.reviews = await response.json();
        // Reset pagination to first page when filters change
        this.pagination.currentPage = 1;
      } catch (error) {
        console.error('Error loading reviews:', error);
        this.showToast('error', 'Load Failed', 'Failed to load reviews. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    /**
     * Load review statistics (pending, approved, rejected, total)
     * Silently fails if unauthorized to avoid disrupting user experience
     * @async
     * @returns {Promise<void>}
     */
    async loadStats() {
      try {
        const adminStore = useAdminAuthStore();
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) return;
        
        const apiUrl = buildApiUrl('/review/admin/reviews/stats');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            return; // Silently fail for stats
          }
          throw new Error('Failed to load stats');
        }
        
        this.stats = await response.json();
      } catch (error) {
        console.error('Error loading stats:', error);
        // Stats failure is non-critical, don't show toast
      }
    },

    /**
     * Build query string from current filter settings
     * @returns {string} URL-encoded query string
     */
    buildQueryString() {
      const params = new URLSearchParams();
      if (this.filters.status !== 'all') params.append('status', this.filters.status);
      if (this.filters.rating !== 'all') params.append('rating', this.filters.rating);
      if (this.filters.productType !== 'all') params.append('product_type', this.filters.productType);
      return params.toString();
    },

    /**
     * Approve a single review
     * @param {number} reviewId - The ID of the review to approve
     * @async
     * @returns {Promise<void>}
     */
    async approveReview(reviewId) {
      if (!this.canInteract) {
        this.showToast('error', 'Unauthorized', 'You are not authorized to approve reviews. Only support and super admin can manage reviews.');
        return;
      }
      
      if (!confirm('Are you sure you want to approve this review?')) return;
      
      try {
        const adminStore = useAdminAuthStore();
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to approve reviews');
          return;
        }
        
        const apiUrl = buildApiUrl(`/review/admin/reviews/${reviewId}/approve`);
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session Expired', 'Your session has expired. Please log in again.');
            adminStore.logout();
            this.$router.push({ name: 'AdminLogin' });
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to approve review');
        }
        
        await Promise.all([this.loadReviews(), this.loadStats()]);
        this.closeModal();
        this.showToast('success', 'Review Approved', 'Review has been approved successfully.');
      } catch (error) {
        console.error('Error approving review:', error);
        this.showToast('error', 'Approval Failed', error.message || 'Failed to approve review. Please try again.');
      }
    },

    async rejectReview(reviewId) {
      if (!this.canInteract) {
        this.showToast('error', 'Unauthorized', 'You are not authorized to reject reviews. Only support and super admin can manage reviews.');
        return;
      }
      
      if (!confirm('Are you sure you want to reject this review?')) return;
      
      try {
        const adminStore = useAdminAuthStore();
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to reject reviews');
          return;
        }
        
        const apiUrl = buildApiUrl(`/review/admin/reviews/${reviewId}/reject`);
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session Expired', 'Your session has expired. Please log in again.');
            adminStore.logout();
            this.$router.push({ name: 'AdminLogin' });
            return;
          }
          throw new Error('Failed to reject review');
        }
        
        await this.loadReviews();
        await this.loadStats();
        this.closeModal();
        this.showToast('success', 'Review Rejected', 'Review has been rejected successfully.');
      } catch (error) {
        console.error('Error rejecting review:', error);
        this.showToast('error', 'Rejection Failed', error.message || 'Failed to reject review. Please try again.');
      }
    },

    /**
     * Approve multiple reviews in bulk
     * @async
     * @returns {Promise<void>}
     */
    async bulkApprove() {
      if (!this.canInteract) {
        this.showToast('error', 'Unauthorized', 'You are not authorized to approve reviews. Only support and super admin can manage reviews.');
        return;
      }
      
      if (this.selectedReviews.length === 0) {
        this.showToast('warning', 'No Selection', 'Please select at least one review to approve.');
        return;
      }
      
      if (!confirm(`Approve ${this.selectedReviews.length} review${this.selectedReviews.length > 1 ? 's' : ''}?`)) return;
      
      try {
        const adminStore = useAdminAuthStore();
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to approve reviews');
          return;
        }
        
        const apiUrl = buildApiUrl('/review/admin/reviews/bulk-approve');
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ review_ids: this.selectedReviews })
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session Expired', 'Your session has expired. Please log in again.');
            adminStore.logout();
            this.$router.push({ name: 'AdminLogin' });
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to bulk approve reviews');
        }
        
        const data = await response.json();
        const updatedCount = data.count || this.selectedReviews.length;
        this.selectedReviews = [];
        await Promise.all([this.loadReviews(), this.loadStats()]);
        this.showToast('success', 'Bulk Approval', `${updatedCount} review${updatedCount > 1 ? 's have' : ' has'} been approved successfully.`);
      } catch (error) {
        console.error('Error bulk approving reviews:', error);
        this.showToast('error', 'Bulk Approval Failed', error.message || 'Failed to approve reviews. Please try again.');
      }
    },

    /**
     * Reject multiple reviews in bulk
     * @async
     * @returns {Promise<void>}
     */
    async bulkReject() {
      if (!this.canInteract) {
        this.showToast('error', 'Unauthorized', 'You are not authorized to reject reviews. Only support and super admin can manage reviews.');
        return;
      }
      
      if (this.selectedReviews.length === 0) {
        this.showToast('warning', 'No Selection', 'Please select at least one review to reject.');
        return;
      }
      
      if (!confirm(`Reject ${this.selectedReviews.length} review${this.selectedReviews.length > 1 ? 's' : ''}?`)) return;
      
      try {
        const adminStore = useAdminAuthStore();
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to reject reviews');
          return;
        }
        
        const apiUrl = buildApiUrl('/review/admin/reviews/bulk-reject');
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ review_ids: this.selectedReviews })
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session Expired', 'Your session has expired. Please log in again.');
            adminStore.logout();
            this.$router.push({ name: 'AdminLogin' });
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to bulk reject reviews');
        }
        
        const data = await response.json();
        const updatedCount = data.count || this.selectedReviews.length;
        this.selectedReviews = [];
        await Promise.all([this.loadReviews(), this.loadStats()]);
        this.showToast('success', 'Bulk Rejection', `${updatedCount} review${updatedCount > 1 ? 's have' : ' has'} been rejected successfully.`);
      } catch (error) {
        console.error('Error bulk rejecting reviews:', error);
        this.showToast('error', 'Bulk Rejection Failed', error.message || 'Failed to reject reviews. Please try again.');
      }
    },

    /**
     * Delete a review permanently
     * @param {number} reviewId - The ID of the review to delete
     * @async
     * @returns {Promise<void>}
     */
    async deleteReview(reviewId) {
      if (!this.canInteract) {
        this.showToast('error', 'Unauthorized', 'You are not authorized to delete reviews. Only support and super admin can manage reviews.');
        return;
      }
      
      if (!confirm('Are you sure you want to delete this review? This action cannot be undone.')) return;
      
      try {
        const adminStore = useAdminAuthStore();
        const token = adminStore.accessToken || localStorage.getItem('adminAccessToken');
        if (!token) {
          this.showToast('error', 'Authentication Required', 'Please log in as admin to delete reviews');
          return;
        }
        
        const apiUrl = buildApiUrl(`/review/admin/reviews/${reviewId}`);
        const response = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session Expired', 'Your session has expired. Please log in again.');
            adminStore.logout();
            this.$router.push({ name: 'AdminLogin' });
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to delete review');
        }
        
        await Promise.all([this.loadReviews(), this.loadStats()]);
        this.showToast('success', 'Review Deleted', 'Review has been deleted successfully.');
      } catch (error) {
        console.error('Error deleting review:', error);
        this.showToast('error', 'Deletion Failed', error.message || 'Failed to delete review. Please try again.');
      }
    },

    /**
     * Open review details modal
     * @param {Object} review - The review object to display
     */
    viewReviewDetails(review) {
      this.selectedReview = review;
    },

    /**
     * Close review details modal
     */
    closeModal() {
      this.selectedReview = null;
    },

    /**
     * Toggle selection of a single review
     * @param {number} reviewId - The ID of the review to toggle
     */
    toggleReviewSelection(reviewId) {
      const index = this.selectedReviews.indexOf(reviewId);
      if (index > -1) {
        this.selectedReviews.splice(index, 1);
      } else {
        this.selectedReviews.push(reviewId);
      }
    },

    /**
     * Toggle selection of all reviews on current page
     */
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedReviews = [];
      } else {
        this.selectedReviews = this.paginatedReviews.map(review => review.review_id);
      }
    },

    /**
     * Reset all filters to default values and reload reviews
     */
    resetFilters() {
      this.filters = {
        status: 'all',
        rating: 'all',
        productType: 'all'
      };
      this.loadReviews();
    },

    /**
     * Preview review text with ellipsis if too long
     * @param {string} text - The review text to preview
     * @returns {string} Preview text (max 100 characters)
     */
    previewText(text) {
      if (!text) return '';
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    },

    /**
     * Format date string to readable format
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date (e.g., "Jan 15, 2024")
     */
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return 'Invalid Date';
      }
    },

    /**
     * Get human-readable rating text
     * @param {number} rating - Rating value (1-5)
     * @returns {string} Rating text description
     */
    getRatingText(rating) {
      const ratings = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Very Good", 5: "Excellent" };
      return ratings[rating] || "Unknown";
    },

    /**
     * Navigate to next page
     */
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.pagination.currentPage++;
      }
    },

    /**
     * Navigate to previous page
     */
    previousPage() {
      if (this.currentPage > 1) {
        this.pagination.currentPage--;
      }
    },

    /**
     * Handle image loading errors by replacing with placeholder
     * @param {Event} event - Image error event
     */
    handleImageError(event) {
      if (event.target && event.target.parentNode) {
        event.target.style.display = 'none';
        const placeholder = document.createElement('div');
        placeholder.className = 'product-image-placeholder';
        placeholder.innerHTML = '<i class="fa-solid fa-image"></i>';
        event.target.parentNode.appendChild(placeholder);
      }
    },

  }
}
</script>

<style scoped>
.review-management {
  padding: 1.5rem;
  background: var(--background-color1);
  min-height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: var(--text-color1);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--text-color2);
}

.authorization-alert {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(220, 53, 69, 0.1);
  border: 2px solid #dc3545;
  border-radius: 8px;
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
}

.authorization-alert i {
  font-size: 1.2rem;
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--background-color2);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.stat-card.pending .stat-icon { color: #ffc107; }
.stat-card.approved .stat-icon { color: #28a745; }
.stat-card.rejected .stat-icon { color: #dc3545; }
.stat-card.total .stat-icon { color: #007bff; }

.stat-content h3 {
  font-size: 1.5rem;
  margin: 0;
  color: var(--text-color1);
}

.stat-content p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.9rem;
}

/* Filter Controls */
.filter-controls {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--background-color2);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color2);
  font-size: 0.9rem;
}

.filter-group select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: var(--background-color1);
  color: var(--text-color1);
}

/* Table Styles */
.reviews-table-container {
  background: var(--background-color2);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--background-color3);
  background: var(--background-color2);
}

.table-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
}

.table-wrapper {
  overflow-x: auto;
}

.reviews-table {
  width: 100%;
  border-collapse: collapse;
}

.reviews-table th,
.reviews-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid var(--background-color3);
  color: var(--text-color1);
}

.reviews-table th {
  background: var(--background-color2);
  font-weight: 600;
  color: var(--text-color2);
  position: sticky;
  top: 0;
  z-index: 10;
}

.reviews-table td {
  background: var(--background-color1);
}

.checkbox-col {
  width: 40px;
  text-align: center;
}


/* Specific column styles */
.product-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color2);
}

.product-image-placeholder.large {
  width: 80px;
  height: 80px;
}

.product-image-placeholder i {
  font-size: 1.2rem;
}

.product-image-placeholder.large i {
  font-size: 2rem;
}

.product-sku {
  display: block;
  font-size: 0.75rem;
  color: var(--text-color2);
  margin-top: 0.25rem;
}

.product-type {
  font-size: 0.8rem;
  color: var(--text-color2);
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.customer-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--border-color-hover);
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.8rem;
}


.rating .stars {
  display: flex;
  gap: 0.1rem;
}

.rating .fa-star {
  font-size: 0.8rem;
}

.rating .fa-star.filled {
  color: #ffc107;
}

.review-title {
  max-width: 200px;
}

.review-preview {
  font-size: 0.8rem;
  color: var(--text-color2);
  margin-top: 0.25rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.status-badge.approved {
  background: #d1edff;
  color: #004085;
}

.status-badge.rejected {
  background: #f8d7da;
  color: #721c24;
}

.actions {
  display: flex;
  gap: 0.25rem;
}

.actions .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

/* Button Styles - Consistent with Admin Theme */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.btn-primary {
  background: var(--border-color-hover);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--border-color-hover3);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
  transform: translateY(-1px);
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover:not(:disabled) {
  background: #138496;
  transform: translateY(-1px);
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover:not(:disabled) {
  background: #e0a800;
  transform: translateY(-1px);
}

.btn-secondary {
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--background-color3);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--background-color2);
  border-color: var(--border-color-hover);
}

.btn-outline {
  background: transparent;
  color: var(--text-color1);
  border: 1px solid var(--background-color3);
}

.btn-outline:hover:not(:disabled) {
  background: var(--background-color2);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid var(--background-color3);
  background: var(--background-color2);
}

.page-info {
  color: var(--text-color2);
  font-weight: 600;
}

/* Modal Styles - Using Admin Theme Colors */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: var(--background-color2);
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
  border: 1px solid var(--background-color3);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--background-color3);
  background: var(--background-color2);
  position: sticky;
  top: 0;
  z-index: 10;
}

.modal-header h3 {
  color: var(--text-color1);
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-color2);
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.close-btn:hover {
  background: var(--background-color3);
  color: var(--text-color1);
}

.modal-body {
  padding: 1.5rem;
  background: var(--background-color2);
}

.detail-section {
  margin-bottom: 2rem;
}

.detail-section h4 {
  margin-bottom: 1rem;
  color: var(--text-color1);
  font-size: 1.2rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--border-color-hover);
}

.detail-section h5 {
  color: var(--text-color1);
  font-size: 1.1rem;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.product-detail,
.customer-detail {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.product-detail img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
}

.customer-avatar.large {
  width: 60px;
  height: 60px;
  font-size: 1.2rem;
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.rating-text {
  font-weight: 600;
  color: var(--text-color1);
}

.review-fulltext {
  line-height: 1.6;
  color: var(--text-color2);
}

.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.metric {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: var(--background-color1);
  border-radius: 6px;
  border: 1px solid var(--background-color3);
}

.metric-label {
  font-weight: 600;
  color: var(--text-color2);
}

.metric-value {
  color: var(--text-color1);
  font-weight: 500;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid var(--background-color3);
  background: var(--background-color2);
  position: sticky;
  bottom: 0;
  z-index: 10;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-color2);
  background: var(--background-color2);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: var(--text-color2);
  opacity: 0.5;
}

.empty-state h4 {
  color: var(--text-color1);
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: var(--text-color2);
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1500;
}

.loading-overlay p {
  color: var(--text-color1);
  margin-top: 1rem;
  font-weight: 500;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--background-color3);
  border-top: 4px solid var(--border-color-hover);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .review-management {
    padding: 1rem;
  }

  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .reviews-table {
    font-size: 0.9rem;
  }

  .reviews-table th,
  .reviews-table td {
    padding: 0.75rem 0.5rem;
  }
}

@media (max-width: 768px) {
  .review-management {
    padding: 0.75rem;
  }

  .page-header h1 {
    font-size: 1.5rem;
  }

  .stats-cards {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 1rem;
  }

  .filter-group {
    width: 100%;
  }

  .table-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
  }

  .table-header h3 {
    font-size: 1.1rem;
  }

  .table-actions {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .table-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .reviews-table {
    min-width: 800px;
    font-size: 0.85rem;
  }

  .reviews-table th,
  .reviews-table td {
    padding: 0.5rem;
    white-space: nowrap;
  }

  .product-info,
  .customer-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .actions {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .actions .btn-sm {
    padding: 0.3rem 0.4rem;
    font-size: 0.75rem;
  }

  .pagination {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }

  .page-info {
    order: -1;
  }

  .modal-content {
    width: 95%;
    max-width: 95%;
    margin: 0.5rem;
    max-height: 95vh;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer .btn {
    width: 100%;
  }

  .metrics {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .review-management {
    padding: 0.5rem;
  }

  .page-header {
    margin-bottom: 1rem;
  }

  .page-header h1 {
    font-size: 1.25rem;
  }

  .page-header p {
    font-size: 0.9rem;
  }

  .stats-cards {
    gap: 0.5rem;
  }

  .stat-card {
    padding: 0.75rem;
  }

  .stat-icon {
    font-size: 1.5rem;
    margin-right: 0.75rem;
  }

  .stat-content h3 {
    font-size: 1.25rem;
  }

  .filter-controls {
    padding: 0.75rem;
  }

  .table-header {
    padding: 0.75rem;
  }

  .reviews-table {
    min-width: 700px;
    font-size: 0.8rem;
  }

  .product-image,
  .product-image-placeholder {
    width: 30px;
    height: 30px;
  }

  .customer-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }

  .modal-content {
    width: 98%;
    max-width: 98%;
    margin: 0.25rem;
  }

  .modal-header h3 {
    font-size: 1.2rem;
  }

  .detail-section h4 {
    font-size: 1rem;
  }
}

@media (max-width: 360px) {
  .reviews-table {
    min-width: 600px;
  }

  .actions .btn-sm {
    padding: 0.25rem 0.3rem;
    font-size: 0.7rem;
  }
}
</style>